import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import content from "../../data/content";
import SectionDivider from "../../components/SectionDivider";
import CtaBanner from "../../components/CtaBanner";
import "./Home.css";

const { home } = content;

function TextWithBreaks({ text }) {
  return text.split("\n").map((line, i, arr) => (
    <span key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </span>
  ));
}

function useScrollReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function RevealSection({ className, children }) {
  const ref = useScrollReveal();
  return (
    <section ref={ref} className={`scroll-reveal ${className}`}>
      {children}
    </section>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const homeRef = useRef(null);

  const onScroll = useCallback(() => {
    const y = window.scrollY;

    // Hero parallax
    if (heroRef.current) {
      const bg = heroRef.current.querySelector(".home-hero__bg");
      const content = heroRef.current.querySelector(".home-hero__content");
      const accent1 = heroRef.current.querySelector(".home-hero__accent--1");
      const accent2 = heroRef.current.querySelector(".home-hero__accent--2");
      const accent3 = heroRef.current.querySelector(".home-hero__accent--3");
      if (bg) bg.style.transform = `translateY(${y * 0.4}px)`;
      if (content) {
        content.style.transform = `translateY(${y * 0.2}px)`;
        content.style.opacity = Math.max(1 - y / 500, 0);
      }
      // Accents drift at different speeds for depth
      if (accent1) accent1.style.transform = `translate(${y * 0.05}px, ${y * 0.12}px)`;
      if (accent2) accent2.style.transform = `translate(${y * -0.04}px, ${y * 0.08}px)`;
      if (accent3) accent3.style.transform = `translate(${y * 0.03}px, ${y * 0.15}px)`;
    }

    // Section parallax glows
    if (homeRef.current) {
      const glows = homeRef.current.querySelectorAll(".home-section__glow");
      glows.forEach((glow) => {
        const rect = glow.parentElement.getBoundingClientRect();
        const offset = rect.top * -0.08;
        glow.style.transform = `translate(-50%, calc(-50% + ${offset}px))`;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  return (
    <div className="home" ref={homeRef}>
      {/* ---- Hero ---- */}
      <section className="home-hero" ref={heroRef}>
        <div className="home-hero__bg" />
        {/* Decorative thin lines */}
        <div className="home-hero__line home-hero__line--left" />
        <div className="home-hero__line home-hero__line--right" />
        {/* Floating accent orbs at different depths */}
        <div className="home-hero__accent home-hero__accent--1" />
        <div className="home-hero__accent home-hero__accent--2" />
        <div className="home-hero__accent home-hero__accent--3" />
        {/* Vignette overlay */}
        <div className="home-hero__vignette" />
        <div className="home-hero__content">
          <h1 className="home-hero__heading">
            <span className="home-hero__brand">{home.hero.headingBrand}</span>
            <span className="home-hero__chinese">{home.hero.headingChinese}</span>
          </h1>
          <p className="home-hero__sub">{home.hero.subheading}</p>
          <div className="home-hero__scroll">
            <span />
          </div>
        </div>
      </section>

      {/* ---- Intro ---- */}
      <RevealSection className="home-section home-intro">
        <div className="home-section__glow" />
        <div className="home-section__inner">
          {home.intro.paragraphs.map((p, i) => (
            <p key={i} className="home-intro__text">
              <TextWithBreaks text={p} />
            </p>
          ))}
        </div>
      </RevealSection>

      <SectionDivider />

      {/* ---- Atmosphere ---- */}
      <RevealSection className="home-section home-atmosphere">
        <div className="home-section__glow home-section__glow--warm" />
        <div className="home-section__inner">
          <h2 className="home-atmosphere__heading">{home.atmosphere.heading}</h2>
          <p className="home-atmosphere__body">
            <TextWithBreaks text={home.atmosphere.body} />
          </p>
        </div>
      </RevealSection>

      <SectionDivider />

      {/* ---- Philosophy ---- */}
      <RevealSection className="home-section home-philosophy">
        <div className="home-section__glow" />
        <div className="home-section__inner">
          <h2 className="home-philosophy__heading">{home.philosophy.heading}</h2>
          <p className="home-philosophy__body">
            <TextWithBreaks text={home.philosophy.body} />
          </p>
        </div>
      </RevealSection>

      <SectionDivider />

      {/* ---- Quick Services ---- */}
      <RevealSection className="home-section home-quick">
        <div className="home-section__glow home-section__glow--warm" />
        <div className="home-section__inner">
          <h2 className="home-quick__heading">{home.quickServices.heading}</h2>
          <p className="home-quick__sub">{home.quickServices.subheading}</p>
          <p className="home-quick__desc">{home.quickServices.description}</p>
          <Link to="/services" className="home-quick__link">
            查看服務項目
          </Link>
        </div>
      </RevealSection>

      <CtaBanner />
    </div>
  );
}
